import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Crown, Sparkles, X } from "lucide-react";
import confetti from "canvas-confetti";

interface AdultFrogCelebrationProps {
  isOpen: boolean;
  onClose: () => void;
  habitName: string;
  adultFrogCount: number;
}

const AdultFrogCelebration = ({ 
  isOpen, 
  onClose, 
  habitName,
  adultFrogCount 
}: AdultFrogCelebrationProps) => {
  const [showPlans, setShowPlans] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger confetti celebration
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const pricePerFrog = 2.90;
  const totalPrice = adultFrogCount * pricePerFrog;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-card border-border overflow-hidden p-0">
        {!showPlans ? (
          <>
            {/* Celebration View */}
            <div className="relative">
              <div className="absolute inset-0 bg-sunset-gradient opacity-20" />
              <div className="relative p-8 text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-golden rounded-full flex items-center justify-center shadow-float animate-float">
                  <span className="text-5xl">🐸</span>
                </div>
                
                <div className="inline-flex items-center gap-2 bg-golden/20 rounded-full px-4 py-2 mb-4">
                  <Crown className="w-4 h-4 text-golden" />
                  <span className="text-sm font-bold text-foreground">PARABÉNS!</span>
                </div>
                
                <DialogHeader className="space-y-2">
                  <DialogTitle className="text-2xl md:text-3xl font-display text-center">
                    Você criou um hábito! 🎉
                  </DialogTitle>
                  <DialogDescription className="text-center text-base">
                    Seu sapo do hábito <strong className="text-foreground">"{habitName}"</strong> chegou à fase adulta! 
                    Isso significa que você manteve esse hábito por mais de 31 dias consecutivos.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="mt-6 p-4 bg-secondary/50 rounded-2xl">
                  <p className="text-sm text-muted-foreground mb-2">
                    Estudos mostram que hábitos que duram mais de 30 dias têm 90% de chance de se tornarem permanentes!
                  </p>
                  <div className="flex justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Sparkles key={i} className="w-4 h-4 text-golden animate-pulse" style={{ animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                </div>
                
                <div className="mt-6 flex flex-col gap-3">
                  <Button variant="coral" size="lg" onClick={() => setShowPlans(true)} className="w-full">
                    Ver planos de assinatura
                  </Button>
                  <Button variant="ghost" onClick={onClose} className="w-full">
                    Continuar grátis por agora
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Plans View */}
            <div className="p-6">
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-4 top-4"
                onClick={() => setShowPlans(false)}
              >
                <X className="w-4 h-4" />
              </Button>
              
              <DialogHeader className="mb-6">
                <DialogTitle className="text-xl font-display text-center">
                  Planos de Assinatura
                </DialogTitle>
                <DialogDescription className="text-center">
                  Pague apenas pelo sucesso! Cobramos um valor simbólico por cada sapo adulto que você cultivar.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Pay per frog model */}
                <div className="pond-card border-2 border-primary relative">
                  <div className="absolute -top-3 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold">
                    RECOMENDADO
                  </div>
                  
                  <div className="flex items-start justify-between mb-4 pt-2">
                    <div>
                      <h3 className="font-display font-bold text-lg">Por Sapo Adulto</h3>
                      <p className="text-sm text-muted-foreground">Pague só quando tiver sucesso</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-foreground">R$ {pricePerFrog.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">por sapo/mês</div>
                    </div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-xl p-3 mb-4">
                    <div className="flex justify-between items-center text-sm">
                      <span>Seus sapos adultos:</span>
                      <span className="font-bold">{adultFrogCount} 🐸</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span>Total mensal:</span>
                      <span className="font-bold text-coral">R$ {totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-4">
                    {[
                      "Sem limite de sapos",
                      "Lagoas ilimitadas",
                      "Estatísticas avançadas",
                      "Prioridade no suporte",
                      "Cancele quando quiser",
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button variant="coral" className="w-full">
                    Assinar agora
                  </Button>
                </div>
                
                {/* Free tier reminder */}
                <div className="pond-card bg-secondary/30">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-display font-bold">Plano Gratuito</h3>
                      <p className="text-sm text-muted-foreground">Perfeito para começar</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-foreground">R$ 0</div>
                      <div className="text-xs text-muted-foreground">para sempre</div>
                    </div>
                  </div>
                  
                  <ul className="space-y-1">
                    {[
                      "Até 3 hábitos ativos",
                      "1 lagoa pessoal",
                      "Estatísticas básicas",
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                Sapos hibernando não são cobrados. Você só paga por hábitos que está cultivando ativamente.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdultFrogCelebration;
